import type { Coords, Placement } from '@floating-ui/react-dom';
import classNames from 'classnames';
import React, { forwardRef } from 'react';
import * as styles from './FloatingArrow.module.scss';

/**
 * Compensate for the border of the floating element.
 * The value is the size of the border.
 *
 * This is needed due to the floating element having a border.
 * ```
 * <div style={{ position: 'relative', border: '1px solid #ccc' }}>
 *   <div>Content goes here</div>
 *   <FloatingArrow />
 * </div>
 * ```
 */
const BORDER_COMPENSATION_SIZE = 1;

interface FloatingArrowProps {
  placement: Placement;
  arrowData?: Partial<Coords>;
  width?: number;
  height?: number;
  /**
   * Distance between the arrow and the anchor element.
   * @default 0
   */
  padding?: number;
}

const FloatingArrow = forwardRef<SVGSVGElement, FloatingArrowProps>(
  ({ placement, arrowData, width = 16, height = 8, padding = 0 }, ref) => {
    const x = width * 0.4;
    const y = height / 5;

    return arrowData ? (
      <svg
        ref={ref}
        data-placement={placement}
        aria-hidden="true"
        role="presentation"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className={styles.arrow}
        style={{
          left: arrowData.x,
          top: arrowData.y,
          paddingTop: padding > 0 ? padding : undefined,
          [styles.borderCompensationProperty]: `${BORDER_COMPENSATION_SIZE}px`,
        }}
      >
        <path
          className={styles.arrowBg}
          d={`M0,${height} H${width} L${width - x},${y} Q${
            width / 2
          },0 ${x},${y} Z`}
        />
        <path
          fill="none"
          className={classNames(styles.arrowBg, styles.arrowStroke)}
          strokeWidth="1"
          strokeLinejoin="round"
          d={`M${width},${height} L${width - x},${y} Q${
            width / 2
          },0 ${x},${y} L0,${height}`}
        />
        <line
          x1="0"
          y1={height}
          x2={width}
          y2={height}
          className={styles.arrowStrokeBottom}
          strokeWidth="1"
        />
      </svg>
    ) : null;
  },
);

export default FloatingArrow;
